/**
 * useDraftAutosave
 * --------------------------------------------------------------
 * Persists in-progress form data to localStorage so an accidental
 * window close / tab crash / refresh does not lose typed work.
 *
 * Scope: per-user + per-form-type. Drafts auto-expire after 7 days.
 *
 * Usage:
 *   const draft = useDraftAutosave({
 *     key: 'sales-invoice',           // unique per form
 *     data: formData,                 // current form state
 *     enabled: isCreateOpen && !editingDocument,  // only when creating new
 *     userId: user?.id,
 *   });
 *
 *   // restore
 *   if (draft.hasDraft) <RestoreBanner ... />
 *
 *   // on successful submit
 *   draft.clear();
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const PREFIX = 'bizcore_draft__';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DEBOUNCE_MS = 800;

function _storageKey(userId, key) {
  return `${PREFIX}${userId || 'anon'}__${key}`;
}

function _isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Heuristic: a form is "meaningfully filled" if at least one field
 * carries non-empty, non-default content. We compare against the
 * `initial` snapshot if provided, otherwise treat any non-empty
 * primitive as content.
 */
function _hasContent(data, initial) {
  if (!data || typeof data !== 'object') return false;
  for (const k of Object.keys(data)) {
    const v = data[k];
    // Skip date fields that auto-default to today (very low signal)
    if (k === 'date' || k === 'valid_until' || k === 'due_date') continue;
    if (k === 'lines' && Array.isArray(v)) {
      // Lines have content if any line has a description / non-default qty/price
      const hasLine = v.some(
        (l) =>
          (l?.description && String(l.description).trim() !== '') ||
          Number(l?.unit_price) > 0 ||
          Number(l?.amount) > 0
      );
      if (hasLine) return true;
      continue;
    }
    if (_isEmpty(v)) continue;
    if (initial && JSON.stringify(initial[k]) === JSON.stringify(v)) continue;
    return true;
  }
  return false;
}

export function useDraftAutosave({ key, data, enabled = true, userId, initial = null }) {
  const storeKey = useMemo(() => _storageKey(userId, key), [userId, key]);
  const [savedAt, setSavedAt] = useState(null);
  const [existingDraft, setExistingDraft] = useState(null); // {data, savedAt} from a prior session
  const [restored, setRestored] = useState(false);
  const debounceRef = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // On mount / enable, look up any previously stored draft.
  useEffect(() => {
    if (!enabled) {
      setExistingDraft(null);
      return;
    }
    try {
      const raw = localStorage.getItem(storeKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt) return;
      const age = Date.now() - new Date(parsed.savedAt).getTime();
      if (age > TTL_MS) {
        localStorage.removeItem(storeKey);
        return;
      }
      if (!_hasContent(parsed.data, initial)) return;
      setExistingDraft(parsed);
    } catch (e) {
      // corrupt — wipe
      try { localStorage.removeItem(storeKey); } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, enabled]);

  // Debounced persistence on every data change while enabled.
  useEffect(() => {
    if (!enabled) return;
    if (!_hasContent(data, initial)) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        const payload = {
          data,
          savedAt: new Date().toISOString(),
          key,
        };
        localStorage.setItem(storeKey, JSON.stringify(payload));
        setSavedAt(payload.savedAt);
      } catch (e) {
        // Quota / privacy mode — silently ignore.
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled, storeKey]);

  // Final flush on unload — best effort, catches abrupt closes between
  // debounce ticks. (Browsers allow synchronous localStorage writes here.)
  useEffect(() => {
    if (!enabled) return;
    const flush = () => {
      try {
        if (!_hasContent(data, initial)) return;
        localStorage.setItem(
          storeKey,
          JSON.stringify({ data, savedAt: new Date().toISOString(), key })
        );
      } catch (_) {}
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled, storeKey]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(storeKey); } catch (_) {}
    setSavedAt(null);
    setExistingDraft(null);
    setRestored(false);
  }, [storeKey]);

  const restore = useCallback(() => {
    if (!existingDraft) return null;
    setRestored(true);
    return existingDraft.data;
  }, [existingDraft]);

  const discard = useCallback(() => {
    clear();
  }, [clear]);

  return {
    hasDraft: !!existingDraft && !restored,
    draftSavedAt: existingDraft?.savedAt || null,
    lastAutoSavedAt: savedAt,
    restore,
    discard,
    clear,
  };
}

export default useDraftAutosave;
